import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { loadSearchHistory, normalizeFilterValue, removeFromHistory, saveSearchHistory } from "@/hooks/use-column-filter";
import { ColumnFilterPopover } from "@/components/base/column-filter-popover";
import type { SortDirection } from "@/components/base/column-filter-popover";
import { ColumnHeader } from "@/components/base/column-header";
import { Button, Input, Toggle, TooltipTrigger } from "@its-engineering-and-development/ui-foundation/components";
import { useNotifications } from "@its-engineering-and-development/ui-foundation/providers";
import { DropdownSelector } from "@app/components/base/dropdown-selector/dropdown-selector";
import { DataTableShell } from "@app/components/base/data-table/data-table";
import { Drawer } from "@app/components/base/drawer/drawer";
import { ConfirmDialog } from "@app/components/base/confirm-dialog";
import { BlockedActionTooltip } from "@app/components/base/blocked-action-tooltip";
import { DeleteCategoryDialog } from "./delete-category-dialog";
import type { TablePageInfo } from "@its-engineering-and-development/ui-foundation/components";
import { useCategories, useCategoryDetail, useUpdateCategory, useCreateCategory, useDeleteCategory } from "@app/hooks/use-categories";
import { ACTIONS } from "@/auth";
import { useCan } from "@/hooks/use-can";
import { isProtectedCategory } from "@app/constants/expense-categories";
import { CATEGORY_GROUPS } from "@app/constants/category-groups";
import { DEFAULT_CATEGORY_GROUP, normalizeCategoryGroup } from "@app/constants/category-groups";
import type { CategoriesColumnFilter, CategoriesSortBy, CategoriesSortOrder } from "@app/hooks/use-categories";
import { CATEGORY_STATUS } from "@app/types/categories";
import type { Category, CategoryStatus } from "@app/types/categories";
import { GLOBAL_DATE_FORMAT } from "@/constants/date-formats";
import { formatDate } from "@/utils/date";

// ─── Types ────────────────────────────────────────────────────────────────────

type CategoryColumnKey = "categoryName" | "categoryGroup" | "status" | "accountCode" | "updatedAt" | "updatedBy";

// ─── Constants ────────────────────────────────────────────────────────────────

// Columns that support server-side text/enum filtering
const FILTERABLE_COLUMNS = new Set<CategoryColumnKey>(["categoryName", "categoryGroup", "status", "accountCode", "updatedAt", "updatedBy"]);

// Columns that support server-side sorting
const SORTABLE_COLUMNS = new Set<CategoryColumnKey>(["categoryName", "categoryGroup", "status", "accountCode", "updatedAt", "updatedBy"]);

const CATEGORIES_SEARCH_HISTORY_KEY = "expense-admin:categories-column-search-history";

const FOUNDATIONAL_DELETE_TOOLTIP =
    "This is a foundational category and cannot be deleted. To hide it from active use, deactivate it using the toggle in the upper right corner.";

const CATEGORY_STATUS_FILTER_OPTIONS = [
    { label: "Active", value: CATEGORY_STATUS.Active },
    { label: "Deactivated", value: CATEGORY_STATUS.Deactivated },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildApiFilter(applied: Partial<Record<CategoryColumnKey, string>>): CategoriesColumnFilter {
    const filter: CategoriesColumnFilter = {};
    if (applied.categoryName) filter.categoryName = applied.categoryName;
    if (applied.categoryGroup) filter.categoryGroup = applied.categoryGroup;
    if (applied.status) filter.status = applied.status as CategoryStatus;
    if (applied.accountCode) filter.accountCode = applied.accountCode;
    if (applied.updatedAt) filter.updatedAt = applied.updatedAt;
    if (applied.updatedBy) filter.updatedByUserName = applied.updatedBy;
    return filter;
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function IconDownload() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M12 3v12m0 0-4-4m4 4 4-4M3 17v2a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2" />
        </svg>
    );
}

function IconCategoryPlus() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18" />
            <path strokeLinecap="round" d="M15 13v4m-2-2h4" />
        </svg>
    );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

function CategoryStatusBadge({ status }: { status: CategoryStatus }) {
    // A "deleted" entry was previously required only to satisfy the exhaustive Record<CategoryStatus, ...>
    // after CATEGORY_STATUS.Deleted was added for accounting-approval; it never rendered here since this
    // page does not list soft-deleted categories. Using Partial drops that entry to avoid the confusion.
    const map: Partial<Record<CategoryStatus, string>> = {
        [CATEGORY_STATUS.Active]: "bg-green-100 text-green-700",
        [CATEGORY_STATUS.Deactivated]: "bg-gray-100 text-gray-600",
    };
    const label: Partial<Record<CategoryStatus, string>> = {
        [CATEGORY_STATUS.Active]: "Active",
        [CATEGORY_STATUS.Deactivated]: "Deactivated",
    };
    return (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${map[status] ?? "bg-gray-100 text-gray-500"}`}>
            {label[status] ?? "—"}
        </span>
    );
}

// ─── Category Row ─────────────────────────────────────────────────────────────

function CategoryRow({ category, onSelect }: { category: Category; onSelect: (categoryId: string) => void }) {
    return (
        <tr
            className="cursor-pointer hover:bg-gray-50"
            tabIndex={0}
            data-category-id={category.categoryId}
            onClick={() => onSelect(category.categoryId)}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect(category.categoryId);
                }
            }}
        >
            <td className="px-4 py-3 font-medium text-blue-600">
                <span className="block max-w-[280px] break-words">{category.categoryName}</span>
            </td>

            <td className="px-4 py-3 text-gray-700">{category.categoryGroup ?? <span className="text-gray-400">—</span>}</td>

            <td className="px-4 py-3">
                <CategoryStatusBadge status={category.status} />
            </td>

            <td className="px-4 py-3 text-gray-700">{category.accountCode ?? <span className="text-gray-400">—</span>}</td>
            <td className="px-4 py-3 text-gray-700">{formatDate(category.updatedAt)}</td>
            <td className="px-4 py-3 text-gray-700">{category.updatedBy?.userName ?? <span className="text-gray-400">—</span>}</td>
        </tr>
    );
}

// ─── Loading Placeholder ──────────────────────────────────────────────────────

// ─── Category Detail Panel ────────────────────────────────────────────────────

function CategoryDetailPanel({
    categoryId,
    isCreating,
    onClose,
    triggerRef,
    onUpdated,
    canWrite,
}: {
    categoryId: string | null;
    isCreating: boolean;
    onClose: () => void;
    triggerRef: RefObject<HTMLElement | null>;
    onUpdated: () => void;
    canWrite: boolean;
}) {
    const { category, isLoading, error: detailError } = useCategoryDetail(isCreating ? null : categoryId);
    const { updateCategory, isLoading: isUpdating, error: updateError, clearError } = useUpdateCategory();
    const { createCategory, isLoading: isCreatingLoading, error: createError, clearError: clearCreateError } = useCreateCategory();
    const { deleteCategory, isLoading: isDeleting } = useDeleteCategory();
    const { showSuccess, showError } = useNotifications();

    const [categoryName, setCategoryName] = useState("");
    const [accountCode, setAccountCode] = useState("");
    const [categoryGroup, setCategoryGroup] = useState<string>(DEFAULT_CATEGORY_GROUP);
    const [baselineValues, setBaselineValues] = useState<{ categoryName: string; accountCode: string; categoryGroup: string; status: CategoryStatus }>({
        categoryName: "",
        accountCode: "",
        categoryGroup: "",
        status: CATEGORY_STATUS.Active,
    });
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<{ categoryName?: string; categoryGroup?: string; accountCode?: string }>({});
    const [categoryStatus, setCategoryStatus] = useState<CategoryStatus>(CATEGORY_STATUS.Active);
    const [pendingDisable, setPendingDisable] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(false);

    const isProcessing = isCreating ? isCreatingLoading : isUpdating;
    const actionError = isCreating ? createError : updateError;
    const displayError = actionError ?? (!isCreating ? detailError : null);
    const isProtected = !isCreating && isProtectedCategory(category?.legacyId ?? null);

    // Initialize form with category data (for update mode)
    useEffect(() => {
        if (!isCreating && !category) return;

        if (isCreating) {
            // Create mode: empty form
            setCategoryName("");
            setAccountCode("");
            setCategoryGroup(DEFAULT_CATEGORY_GROUP);
            setCategoryStatus(CATEGORY_STATUS.Active);
            setBaselineValues({ categoryName: "", accountCode: "", categoryGroup: DEFAULT_CATEGORY_GROUP, status: CATEGORY_STATUS.Active });
            clearCreateError();
            setSuccessMessage(null);
        } else {
            // Update mode: populate with existing data
            const nextStatus = category?.status ?? CATEGORY_STATUS.Active;
            const nextValues = {
                categoryName: category?.categoryName ?? "",
                accountCode: category?.accountCode ?? "",
                categoryGroup: normalizeCategoryGroup(category?.categoryGroup),
                status: nextStatus,
            };

            setCategoryName(nextValues.categoryName);
            setAccountCode(nextValues.accountCode);
            setCategoryGroup(nextValues.categoryGroup);
            setBaselineValues(nextValues);
            setCategoryStatus(nextStatus);
            clearError();
            setSuccessMessage(null);
        }
    }, [category, isCreating, clearError, clearCreateError]);

    const handleToggleChange = useCallback((nextSelected: boolean) => {
        // The toggle only updates local state; the change is persisted when the user presses "Update category".
        setCategoryStatus(nextSelected ? CATEGORY_STATUS.Active : CATEGORY_STATUS.Deactivated);
    }, []);

    const normalizedCategoryName = categoryName.trim();
    const normalizedAccountCode = accountCode.trim();
    const normalizedCategoryGroup = normalizeCategoryGroup(categoryGroup).trim();

    const validateForm = useCallback(() => {
        const errors: { categoryName?: string; categoryGroup?: string; accountCode?: string } = {};
        if (!normalizedCategoryName) {
            errors.categoryName = "Category name is required";
        }
        if (isCreating && !normalizedCategoryGroup) {
            errors.categoryGroup = "Top level category is required";
        }
        if (!normalizedAccountCode) {
            errors.accountCode = "Account code is required";
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    }, [isCreating, normalizedCategoryName, normalizedCategoryGroup, normalizedAccountCode]);

    const hasChanges = isCreating
        ? normalizedCategoryName.length > 0 && normalizedCategoryGroup.length > 0
        : normalizedCategoryName !== baselineValues.categoryName ||
          normalizedAccountCode !== baselineValues.accountCode ||
          normalizedCategoryGroup !== baselineValues.categoryGroup ||
          categoryStatus !== baselineValues.status;

    // Return focus to the triggering row on close
    const handleClose = useCallback(() => {
        onClose();
        triggerRef.current?.focus();
    }, [onClose, triggerRef]);

    const handleCreateCategory = useCallback(async () => {
        if (!validateForm()) return;

        clearCreateError();
        setSuccessMessage(null);

        try {
            await createCategory({
                categoryName: normalizedCategoryName,
                categoryGroup: normalizedCategoryGroup,
                accountCode: normalizedAccountCode || undefined,
                status: CATEGORY_STATUS.Active,
            });

            setCategoryName("");
            setAccountCode("");
            setCategoryGroup(DEFAULT_CATEGORY_GROUP);
            setBaselineValues({ categoryName: "", accountCode: "", categoryGroup: DEFAULT_CATEGORY_GROUP, status: CATEGORY_STATUS.Active });
            setSuccessMessage("Category created successfully");
            onUpdated();

            // Close the drawer after a short delay to show the success message
            setTimeout(() => {
                onClose();
            }, 1500);
        } catch {
            // Error state is already captured by the hook.
        }
    }, [normalizedCategoryName, normalizedCategoryGroup, normalizedAccountCode, validateForm, clearCreateError, onUpdated, createCategory, onClose]);

    const performUpdate = useCallback(async () => {
        if (!category) return;

        clearError();
        setSuccessMessage(null);

        try {
            const updatedCategory = await updateCategory(category.categoryId, {
                categoryName: normalizedCategoryName,
                accountCode: normalizedAccountCode,
                categoryGroup: normalizedCategoryGroup || undefined,
                status: categoryStatus,
            });

            const nextValues = {
                categoryName: updatedCategory.categoryName,
                accountCode: updatedCategory.accountCode ?? "",
                categoryGroup: updatedCategory.categoryGroup ?? "",
                status: updatedCategory.status,
            };

            setCategoryName(nextValues.categoryName);
            setAccountCode(nextValues.accountCode);
            setCategoryGroup(nextValues.categoryGroup);
            setBaselineValues(nextValues);
            setCategoryStatus(updatedCategory.status);
            setSuccessMessage("Category updated successfully");
            onUpdated();
        } catch {
            // Error state is already captured by the hook.
        }
    }, [category, normalizedCategoryName, normalizedAccountCode, normalizedCategoryGroup, categoryStatus, clearError, onUpdated, updateCategory]);

    const handleUpdateCategory = useCallback(() => {
        if (!category || !hasChanges) return;
        if (!validateForm()) return;

        // Deactivating requires confirmation because in-flight expenses may be impacted; the save runs after confirm.
        if (categoryStatus === CATEGORY_STATUS.Deactivated && baselineValues.status !== CATEGORY_STATUS.Deactivated) {
            setPendingDisable(true);
            return;
        }

        void performUpdate();
    }, [category, hasChanges, categoryStatus, baselineValues.status, validateForm, performUpdate]);

    const handleDeleteCategory = useCallback(async () => {
        if (!category) return;

        try {
            await deleteCategory(category.categoryId);
            setPendingDelete(false);
            showSuccess("Category deleted", `"${baselineValues.categoryName}" was deleted.`);
            onUpdated();
            onClose();
        } catch (err) {
            setPendingDelete(false);
            showError("Failed to delete category", err instanceof Error ? err.message : undefined);
        }
    }, [category, baselineValues.categoryName, deleteCategory, showSuccess, showError, onUpdated, onClose]);

    return (
        <>
            <Drawer
                title={isCreating ? "Create Category" : "Update Category"}
                onClose={handleClose}
                maxWidth="520px"
                isLoading={isLoading || isProcessing}
                loadingLabel="Loading category"
                loadingMessage={
                    isCreating ? (isProcessing ? "Creating category…" : "Preparing form…") : isProcessing ? "Updating category…" : "Loading category details…"
                }
                bodyClassName="p-0"
                footer={
                    <div className="flex flex-col items-center gap-3">
                        {isCreating ? (
                            <Button
                                color="primary"
                                size="md"
                                className="w-full"
                                isLoading={isProcessing}
                                isDisabled={isProcessing}
                                onPress={handleCreateCategory}
                            >
                                Create category
                            </Button>
                        ) : (
                            <Button
                                color="primary"
                                size="md"
                                className="w-full"
                                isLoading={isProcessing}
                                isDisabled={!canWrite || !category || !hasChanges || isProcessing}
                                onPress={handleUpdateCategory}
                            >
                                Update category
                            </Button>
                        )}
                        {!isCreating &&
                            (category?.isFoundational ? (
                                <BlockedActionTooltip title={FOUNDATIONAL_DELETE_TOOLTIP}>
                                    {(reveal) => (
                                        <TooltipTrigger
                                            aria-disabled="true"
                                            onPress={reveal}
                                            className="cursor-not-allowed text-sm font-medium text-red-500 opacity-50"
                                        >
                                            Delete category
                                        </TooltipTrigger>
                                    )}
                                </BlockedActionTooltip>
                            ) : (
                                <button
                                    type="button"
                                    disabled={!canWrite || !category || isProcessing}
                                    className="text-sm font-medium text-red-500 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-red-500"
                                    onClick={() => setPendingDelete(true)}
                                >
                                    Delete category
                                </button>
                            ))}
                    </div>
                }
            >
                {/* Error state */}
                {displayError && !isLoading && (
                    <div className="flex h-full items-center justify-center px-6">
                        <p className="text-center text-sm text-red-600">{displayError}</p>
                    </div>
                )}

                {/* Content */}
                {(isCreating || (category && !isLoading)) && (
                    <div className="flex flex-col gap-5 px-5 py-5">
                        {displayError && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{displayError}</div>}
                        {successMessage && (
                            <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{successMessage}</div>
                        )}

                        {/* Data details + Active toggle */}
                        <div className="flex items-center justify-between">
                            <p className="text-lg font-semibold text-gray-900">Data details</p>
                            <div className="flex items-center gap-2">
                                {/* Protected categories (Mileage / Other) cannot be enabled/disabled, so no toggle is rendered. */}
                                {isProtected ? null : isCreating ? (
                                    <Toggle label="Active" isSelected onChange={() => undefined} size="md" isDisabled />
                                ) : (
                                    <Toggle
                                        label={categoryStatus === CATEGORY_STATUS.Active ? "Active" : "Deactivated"}
                                        isSelected={categoryStatus === CATEGORY_STATUS.Active}
                                        onChange={handleToggleChange}
                                        size="md"
                                        isDisabled={!canWrite || isProcessing}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Top level category (group) */}
                        <div>
                            <DropdownSelector
                                label="Top level category"
                                selectedKey={categoryGroup || DEFAULT_CATEGORY_GROUP}
                                data={(() => {
                                    const canonical = [CATEGORY_GROUPS.GENERAL, CATEGORY_GROUPS.BUSINESS_TRAVEL, CATEGORY_GROUPS.MEALS];
                                    const options = canonical.includes(categoryGroup as any) ? canonical : [...canonical, categoryGroup].filter(Boolean);
                                    return options.map((group) => ({
                                        key: group,
                                        label: group,
                                    }));
                                })()}
                                isEditable={!isProcessing}
                                onSelectionChange={(key) => {
                                    setCategoryGroup(key);
                                    setValidationErrors((prev) => {
                                        const next = { ...prev };
                                        delete next.categoryGroup;
                                        return next;
                                    });
                                }}
                                enableSearch={false}
                            />
                            {validationErrors.categoryGroup && <p className="mt-1 text-sm font-medium text-red-600">{validationErrors.categoryGroup}</p>}
                        </div>

                        {/* Category name */}
                        <div>
                            <Input
                                label="Category name"
                                value={categoryName}
                                maxLength={50}
                                onChange={(value) => {
                                    setCategoryName(value.slice(0, 50));
                                    setValidationErrors((prev) => {
                                        const next = { ...prev };
                                        delete next.categoryName;
                                        return next;
                                    });
                                }}
                                isInvalid={!!validationErrors.categoryName}
                                isDisabled={isProcessing || isProtected}
                                locked={isProtected}
                            />
                            {validationErrors.categoryName && <p className="mt-1 text-sm font-medium text-red-600">{validationErrors.categoryName}</p>}
                        </div>

                        {/* Account code */}
                        <div>
                            <Input
                                label="Account code or ledger account"
                                value={accountCode}
                                onChange={(value) => {
                                    setAccountCode(value);
                                    setValidationErrors((prev) => {
                                        const next = { ...prev };
                                        delete next.accountCode;
                                        return next;
                                    });
                                }}
                                isInvalid={!!validationErrors.accountCode}
                                isDisabled={isProcessing}
                            />
                            {validationErrors.accountCode && <p className="mt-1 text-sm font-medium text-red-600">{validationErrors.accountCode}</p>}
                        </div>
                    </div>
                )}
            </Drawer>

            {!isCreating && category && (
                <ConfirmDialog
                    isOpen={pendingDisable}
                    onOpenChange={(open) => {
                        if (!open) setPendingDisable(false);
                    }}
                    title="Deactivate this category?"
                    confirmLabel="Deactivate"
                    busyLabel="Deactivating..."
                    isConfirming={isProcessing}
                    onConfirm={async () => {
                        await performUpdate();
                        setPendingDisable(false);
                    }}
                >
                    In-flight expenses that currently use this category may be impacted. This category will no longer be available for selection until it is
                    re-enabled.
                </ConfirmDialog>
            )}

            {!isCreating && category && (
                <DeleteCategoryDialog
                    isOpen={pendingDelete}
                    categoryName={baselineValues.categoryName}
                    isDeleting={isDeleting}
                    onOpenChange={setPendingDelete}
                    onConfirm={handleDeleteCategory}
                />
            )}
        </>
    );
}

function CategoriesTable({
    categories,
    isLoading,
    pageInfo,
    onPageChange,
    onSelect,
    sortKey,
    sortDirection,
    onSort,
    onFilter,
}: {
    categories: Category[];
    isLoading: boolean;
    pageInfo: TablePageInfo | null;
    onPageChange: (page: number) => void;
    onSelect: (categoryId: string) => void;
    sortKey: CategoriesSortBy | null;
    sortDirection: CategoriesSortOrder;
    onSort: (key: CategoriesSortBy) => void;
    onFilter: (filter: CategoriesColumnFilter) => void;
}) {
    const [activeFilterColumn, setActiveFilterColumn] = useState<CategoryColumnKey | null>(null);
    const [activeFilterAnchor, setActiveFilterAnchor] = useState<HTMLElement | null>(null);
    const [filterInput, setFilterInput] = useState("");
    const [appliedFilters, setAppliedFilters] = useState<Partial<Record<CategoryColumnKey, string>>>({});
    const [searchHistory, setSearchHistory] = useState<Record<string, string[]>>(() => loadSearchHistory(CATEGORIES_SEARCH_HISTORY_KEY));

    const persistHistory = useCallback((next: Record<string, string[]>) => {
        setSearchHistory(next);
        saveSearchHistory(CATEGORIES_SEARCH_HISTORY_KEY, next);
    }, []);

    const openColumnFilter = useCallback(
        (column: CategoryColumnKey, anchorEl: HTMLElement) => {
            if (activeFilterColumn === column) {
                setActiveFilterColumn(null);
                setActiveFilterAnchor(null);
                return;
            }
            setActiveFilterColumn(column);
            setActiveFilterAnchor(anchorEl);
            setFilterInput(appliedFilters[column] ?? "");
        },
        [activeFilterColumn, appliedFilters],
    );

    const closeFilter = useCallback(() => {
        setActiveFilterColumn(null);
        setActiveFilterAnchor(null);
    }, []);

    const applyColumnFilter = useCallback(
        (value?: string) => {
            if (!activeFilterColumn) return;
            const val = normalizeFilterValue(value ?? filterInput);
            const next = { ...appliedFilters };
            if (val) {
                next[activeFilterColumn] = val;
                persistHistory({
                    ...searchHistory,
                    [activeFilterColumn]: [val, ...(searchHistory[activeFilterColumn] ?? []).filter((v) => v !== val)],
                });
            } else {
                delete next[activeFilterColumn];
            }
            setAppliedFilters(next);
            closeFilter();
            onFilter(buildApiFilter(next));
        },
        [activeFilterColumn, filterInput, appliedFilters, searchHistory, persistHistory, closeFilter, onFilter],
    );

    const clearColumnFilter = useCallback(() => {
        if (!activeFilterColumn) return;
        const next = { ...appliedFilters };
        delete next[activeFilterColumn];
        setAppliedFilters(next);
        closeFilter();
        onFilter(buildApiFilter(next));
    }, [activeFilterColumn, appliedFilters, closeFilter, onFilter]);

    const removeSaved = useCallback(
        (value: string) => {
            if (!activeFilterColumn) return;
            persistHistory(removeFromHistory(searchHistory, activeFilterColumn, value));
        },
        [activeFilterColumn, searchHistory, persistHistory],
    );

    const colHeader = (
        key: CategoryColumnKey,
        label: string,
        opts?: { filterOptions?: Array<{ label: string; value: string }>; inputType?: "text" | "date"; hint?: string },
    ) => {
        const isSortable = SORTABLE_COLUMNS.has(key);
        const isSortActive = isSortable && sortKey === (key as CategoriesSortBy);
        const isFilterable = FILTERABLE_COLUMNS.has(key);
        const hasActiveFilter = key in appliedFilters;

        return [
            <ColumnHeader
                key={`${key}-header`}
                label={label}
                isFilterable={isFilterable}
                isFilterActive={hasActiveFilter}
                isFilterOpen={activeFilterColumn === key}
                onFilterPress={(anchorEl) => openColumnFilter(key, anchorEl)}
                isSortable={isSortable}
                isSortActive={isSortActive}
                sortDirection={isSortActive ? (sortDirection as SortDirection) : undefined}
                onSort={() => onSort(key as CategoriesSortBy)}
            />,
            isFilterable ? (
                <ColumnFilterPopover
                    key={`${key}-filter`}
                    isOpen={activeFilterColumn === key}
                    anchorEl={activeFilterAnchor}
                    columnLabel={label}
                    hint={opts?.hint}
                    inputType={opts?.inputType ?? "text"}
                    options={opts?.filterOptions}
                    searchValue={filterInput}
                    savedValues={opts?.filterOptions ? undefined : (searchHistory[key] ?? [])}
                    onSearchChange={setFilterInput}
                    onClose={closeFilter}
                    onApply={applyColumnFilter}
                    onClear={clearColumnFilter}
                    onRemoveSavedValue={removeSaved}
                />
            ) : null,
        ];
    };

    return (
        <DataTableShell
            tabName="categories"
            isLoading={isLoading}
            loadingDescription="Please wait while the categories load."
            minWidth="860px"
            pageInfo={pageInfo}
            onPageChange={onPageChange}
            isEmpty={categories.length === 0}
            hasActiveFilters={Object.keys(appliedFilters).length > 0}
            emptyMessage={Object.keys(appliedFilters).length > 0 ? "No categories match the current filters." : "No categories found."}
            header={
                <thead className="relative z-30 border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500">
                    <tr>
                        {colHeader("categoryName", "Category")}
                        {colHeader("categoryGroup", "Category Group")}
                        {colHeader("status", "Status", { filterOptions: CATEGORY_STATUS_FILTER_OPTIONS })}
                        {colHeader("accountCode", "Code")}
                        {colHeader("updatedAt", "Modified date", { inputType: "date", hint: `Format: ${GLOBAL_DATE_FORMAT.toUpperCase()}` })}
                        {colHeader("updatedBy", "Modified by")}
                    </tr>
                </thead>
            }
        >
            <tbody className="divide-y divide-gray-100">
                {categories.map((category) => (
                    <CategoryRow key={category.categoryId} category={category} onSelect={onSelect} />
                ))}
            </tbody>
        </DataTableShell>
    );
}

// ─── Categories Page ──────────────────────────────────────────────────────────

export function CategoriesPage() {
    const canWrite = useCan(ACTIONS.manage.categories.write);
    const { categories, pageInfo, currentPage, sort, isLoading, error, setPage, setSort, setColumnFilter, refresh } = useCategories();
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const triggerRef = useRef<HTMLElement | null>(null);

    const tablePageInfo: TablePageInfo | null = pageInfo
        ? {
              totalCount: pageInfo.totalCount,
              offset: currentPage * pageInfo.limit,
              limit: pageInfo.limit,
              hasNextPage: pageInfo.hasNextPage,
          }
        : null;

    const handleSort = useCallback(
        (key: CategoriesSortBy) => {
            let next: { sortBy: CategoriesSortBy; sortOrder: CategoriesSortOrder } | null;
            if (sort?.sortBy !== key) {
                next = { sortBy: key, sortOrder: "asc" };
            } else if (sort.sortOrder === "asc") {
                next = { sortBy: key, sortOrder: "desc" };
            } else {
                next = null; // 3rd click clears sort
            }
            setSort(next);
        },
        [sort, setSort],
    );

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Categories</h1>
                    <p className="mt-2 text-sm text-gray-600">Manage the expense categories shown to users of the system and map to your account codes.</p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                    <Button color="secondary" size="md" isDisabled title="Export not yet supported" iconLeading={IconDownload}>
                        Export
                    </Button>
                    <Button color="primary" size="md" iconLeading={IconCategoryPlus} onPress={() => setIsCreating(true)}>
                        Add Category
                    </Button>
                </div>
            </div>

            {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

            <CategoriesTable
                categories={categories}
                isLoading={isLoading}
                pageInfo={tablePageInfo}
                onPageChange={setPage}
                onSelect={(categoryId) => {
                    triggerRef.current = document.querySelector<HTMLElement>(`[data-category-id="${CSS.escape(categoryId)}"]`);
                    setSelectedCategoryId(categoryId);
                }}
                sortKey={sort?.sortBy ?? null}
                sortDirection={sort?.sortOrder ?? "asc"}
                onSort={handleSort}
                onFilter={setColumnFilter}
            />

            {/* Category detail drawer */}
            {(selectedCategoryId || isCreating) && (
                <CategoryDetailPanel
                    categoryId={selectedCategoryId}
                    isCreating={isCreating}
                    canWrite={canWrite}
                    onClose={() => {
                        setSelectedCategoryId(null);
                        setIsCreating(false);
                    }}
                    triggerRef={triggerRef}
                    onUpdated={refresh}
                />
            )}
        </div>
    );
}
